<template>
  <div>
    <div class="columns is-marginless is-paddingless section section-details">
      <div class="column is-10">
        <div class="card mgt-20px" id="generalInformations">
          <header class="header-card">
            <p>
              {{ $t('detailBailleur.informationsGenerales.title')}}
            </p>
          </header>
          <info-generales :bailleur="bailleur"></info-generales>
        </div>
        <div class="card mgt-20px" id="contacts">
          <header class="header-card">
            <p>
              {{ $t('detailBailleur.contacts.title')}}
            </p>
          </header>
          <contacts :tier="bailleur" isFromBailleur></contacts>
        </div>
        <div class="card mgt-20px" id="bankDetails">
          <header class="header-card">
            <p>
              {{ $t('detailBailleur.infoBancaire.title')}}
            </p>
          </header>
          <info-bancaires :bailleur="bailleur"></info-bancaires>
        </div>
        <div class="columns mgt-20px">
          <div class="column is-12">
            <div class="card">
              <header class="header-card">
                <p>
                  {{ $t('detailBailleur.infoCompta.title')}}
                </p>
              </header>
              <info-comptables :bailleur="bailleur" :isBailleur="true"></info-comptables>
            </div>
          </div>
        </div>
        <div class="card mgt-20px">
          <header class="header-card">
            <p>
              {{ $t('detailBailleur.infoBlocagePaiement.title')}}
            </p>
          </header>
          <info-blocage-paiement :bailleur="bailleur"></info-blocage-paiement>
        </div>
        <div class="card mgt-20px" v-if="isRetrocessionAlfa">
          <header class="header-card">
            <p>
              {{ $t('detailBailleur.retrocession.title')}}
            </p>
          </header>
          <div class="card-content">
            <div class="columns is-multiline">
              <div class="column is-6">
                <b-field class="has-text-default">
                  <span class="is-italic">
                    {{ $t('detailBailleur.retrocession.pourcentageRetrocession') }}
                  </span>
                  <span class="has-text-weight-semibold mgl-10px"
                    v-if="bailleur.pourcentageRetrocession">
                    {{ bailleur.pourcentageRetrocession | formatPercent}}
                  </span>
                </b-field>
              </div>
            </div>
          </div>
        </div>
        <div class="card mgt-20px" id="comments">
          <header class="header-card">
            <p>
              {{ $t('detailBailleur.commentaires.title')}}
            </p>
          </header>
          <div class="card-content has-text-default is-size-7">
            {{ bailleur.commentairesInternes}}
          </div>
        </div>
        <div class="card mgt-20px" id="attachments">
          <header class="header-card">
            <p>
              {{ $t('detailBailleur.piecesJointes.title')}}
            </p>
          </header>
          <div class="card-content">
            <app-pieces-jointes readOnly
                                deletable
                                autoSave
                                :checkPermissions=checkPermissions(permsBailleur.maj)
            />
          </div>
        </div>
      </div>
      <div class="column is-2">
        <div class="is-fixed mgt-20px">
          <dots-menu :steps="steps">
          </dots-menu>
          <information-create-update-user :data="bailleur" class="mgr-20px">
          </information-create-update-user>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import DotsMenu from '@/components/general/dotsMenu';
import infoGenerales from '@/components/baux/bailleur/detailBailleur/infoGenerales';
import contacts from '@/components/tiers/contacts/contacts';
import infoBancaires from '@/components/baux/bailleur/detailBailleur/infoBancaires';
import infoComptables from '@/components/baux/bailleur/detailBailleur/infoComptables';
import infoBlocagePaiement from '@/components/baux/bailleur/detailBailleur/infoBlocagePaiement';
import PiecesJointes from '@/components/general/piecesJointes';
import { BAILLEUR } from '@/configuration/baux/permissionsBauxConf';
import { createNamespacedHelpers } from 'vuex';
import InformationCreateUpdateUser from '@/components/general/informationCreateUpdateUser';
import { PARAMETRES_INTERNE_KEYS } from '@/store/modules/parametres-mutation-type';
import messageErreur from '@/services/general/messageErreur';

const permissions = createNamespacedHelpers('permissions');
const Parametres = createNamespacedHelpers('parametres');

export default {
  name: 'detailBailleur',
  props: {
    bailleur: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      permsBailleur: BAILLEUR,
      steps: [
        {
          name: this.$i18n.t('detailBailleur.steps.infoGenerales'),
          ref: '#generalInformations',
          active: true,
        },
        {
          name: this.$i18n.t('detailBailleur.steps.contacts'),
          ref: '#contacts',
          active: false,
        },
        {
          name: this.$i18n.t('detailBailleur.steps.infoBancaire'),
          ref: '#bankDetails',
          active: false,
        },
        {
          name: this.$i18n.t('detailTiers.steps.commande'),
          ref: '#infoCommande',
          active: false,
        },
        {
          name: this.$i18n.t('detailBailleur.steps.commentaires'),
          ref: '#comments',
          active: false,
        },
        {
          name: this.$i18n.t('detailBailleur.steps.piecesJointes'),
          ref: '#attachments',
          active: false,
        },
      ],
    };
  },
  components: {
    DotsMenu,
    infoGenerales,
    contacts,
    infoBancaires,
    infoComptables,
    infoBlocagePaiement,
    appPiecesJointes: PiecesJointes,
    InformationCreateUpdateUser,
  },
  computed: {
    ...permissions.mapGetters({
      checkPermissions: 'checkPermissions',
    }),
    ...Parametres.mapGetters({
      parametresInterne: 'parametresInterne',
    }),
    isRetrocessionAlfa() {
      return this.parametresInterne(
        PARAMETRES_INTERNE_KEYS.retrocessionAlfa.key,
      );
    },
  },
  methods: {
    ...Parametres.mapActions({
      loadParametresInterne: 'loadParametresInterne',
    }),
  },
  async mounted() {
    const loadingComponent = this.$buefy.loading.open();
    try {
      await this.loadParametresInterne([
        PARAMETRES_INTERNE_KEYS.retrocessionAlfa.key]);
      loadingComponent.close();
    } catch (e) {
      messageErreur(this.$i18n);
      loadingComponent.close();
      throw e;
    }
  },
};
</script>

<style scoped>

</style>
